import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdNavigateBefore,
  MdNavigateNext,
} from "react-icons/md";
import clsx from "clsx";
import { ICON_SIZES } from "src/helpers/constants";
import Button from "../Button/Button";
import { EButtonKind } from "../Button/ButtonTypes";

// Configuración de comportamiento
const SCROLL_TOP_THRESHOLD = 5;
const SCROLL_BOTTOM_HIDE_THRESHOLD = 200;
const SCROLL_TRIGGER_THRESHOLD = 100;
const INACTIVITY_TIMEOUT = 3000;

const ScrollUpDown: React.FC = () => {
  const [showButtons, setShowButtons] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);
  const [contentWrapperSize, setContentWrapperSize] = useState<number>(0);

  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const isAtTop = scrollTop <= SCROLL_TOP_THRESHOLD;
    const isNearBottom = distanceFromBottom <= SCROLL_BOTTOM_HIDE_THRESHOLD;
    const calculation =
      contentWrapperSize -
      SCROLL_BOTTOM_HIDE_THRESHOLD -
      SCROLL_TRIGGER_THRESHOLD -
      (SCROLL_BOTTOM_HIDE_THRESHOLD + SCROLL_TRIGGER_THRESHOLD);

    const shouldShow =
      !isAtTop &&
      !isNearBottom &&
      scrollTop > SCROLL_TRIGGER_THRESHOLD &&
      calculation >= 500;

    if (shouldShow) {
      setShowButtons(true);
      resetInactivityTimer();
    } else {
      setShowButtons(false);
    }

    setLastScrollTop(scrollTop);
  };

  const resetInactivityTimer = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => {
      setShowButtons(false);
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, [lastScrollTop]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    const container = document.getElementById("content-wrapper");
    if (!container) return;

    setContentWrapperSize(container?.clientHeight);
  }, [lastScrollTop]);

  return (
    <AnimatePresence>
      {showButtons && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className={clsx([
            "fixed",
            "bottom-[120px]",
            "right-4",
            "flex",
            "flex-col",
            "gap-1",
            "z-[90]",
            "md:hidden",
          ])}
        >
          <Button
            onClick={scrollToTop}
            className="!rounded-full opacity-95"
            kind={EButtonKind.SAVE}
            icon={
              <MdNavigateBefore
                className={clsx([
                  "rotate-90",
                  // "dark:text-purple-400",
                  // "text-red-400",
                ])}
                size={ICON_SIZES.MD}
              />
            }
          />
          <Button
            onClick={scrollToBottom}
            className="!rounded-full opacity-95"
            kind={EButtonKind.SAVE}
            icon={
              <MdNavigateNext
                className={clsx([
                  "rotate-90",
                  // "dark:text-purple-400",
                  // "text-red-400",
                ])}
                size={ICON_SIZES.MD}
              />
            }
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollUpDown;
